import { Lancamento, Pagamento, StatusPagamento, TipoLancamento, TipoStatusLancamento } from "../../services/tipos";

export interface ParteFatura {
    nome: string;
    documento?: string;
    email?: string;
    telefone?: string;
    endereco?: string;
}

export interface ItemFatura {
    descricao: string;
    detalhe?: string;
    unidade: string;
    quantidade: number;
    valorUnitario: number;
}

export interface PixFatura {
    copiaECola: string;
    imagem?: string | null;
}

export interface ParcelaFatura {
    parcela: string;
    forma: string;
    vencimento?: string | Date | null;
    observacoes?: string;
    valor: number;
    emAberto: boolean;
    pix?: PixFatura | null;
}

export interface DadosFatura {
    id: string;
    numero: string;
    situacao: string;
    emissao: Date;
    emissor: ParteFatura;
    cliente: ParteFatura | null;
    itens: ItemFatura[];
    parcelas: ParcelaFatura[];
    formaPadrao: string;
    condicao: string;
    idFilial?: string | null;
    pixUnico?: PixFatura | null;
}

export const valorEmAbertoDaFatura = (dados: DadosFatura): number =>
    dados.parcelas.filter(p => p.emAberto).reduce((soma, p) => soma + p.valor, 0);

export const LIMITE_DOCUMENTOS_EM_LOTE = 200;

export const SITUACAO_PAGA = "PAGA";
export const SITUACAO_EM_ATRASO = "EM ATRASO";
export const SITUACAO_EM_ABERTO = "EM ABERTO";

export const montarEnderecoCompleto = (dados: {
    logradouro?: string;
    rua?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    cep?: string;
} | null | undefined): string => {
    if (!dados) return "";
    const partes: string[] = [];
    const rua = dados.logradouro || dados.rua;
    if (rua) {
        let linha = rua;
        if (dados.numero) linha += `, ${dados.numero}`;
        if (dados.complemento) linha += ` - ${dados.complemento}`;
        partes.push(linha);
    }
    if (dados.bairro) partes.push(dados.bairro);
    const cidadeEstado = [dados.cidade, dados.estado].filter(Boolean).join("/");
    if (cidadeEstado) partes.push(cidadeEstado);
    if (dados.cep) partes.push(`CEP: ${dados.cep}`);
    return partes.join(" - ");
};

export const numeroDaFatura = (id?: string | null): string => {
    const base = (id ?? "").replace(/-/g, "").substring(0, 8).toUpperCase();
    return base || "N/A";
};

export const situacaoDoPagamento = (status?: StatusPagamento): string => {
    if (status === StatusPagamento.Pago) return SITUACAO_PAGA;
    if (status === StatusPagamento.EmAtraso) return SITUACAO_EM_ATRASO;
    return SITUACAO_EM_ABERTO;
};

export const situacaoDoLancamento = (lancamento: Lancamento, hoje: Date = new Date()): string => {
    if (lancamento.status === TipoStatusLancamento.Resolvido) return SITUACAO_PAGA;
    const vencimento = lancamento.dataRealizado ? new Date(lancamento.dataRealizado) : null;
    if (vencimento && !isNaN(vencimento.getTime()) && vencimento.getTime() < inicioDoDia(hoje).getTime()) return SITUACAO_EM_ATRASO;
    return SITUACAO_EM_ABERTO;
};

const inicioDoDia = (data: Date) => new Date(data.getFullYear(), data.getMonth(), data.getDate());

export const formaDePagamento = (asaasPaymentId?: string | null): string =>
    asaasPaymentId ? "Boleto / PIX" : "A combinar";

export const emissorDaEmpresa = (empresa: any, nomeFallback: string): ParteFatura => ({
    nome: empresa?.nome || nomeFallback,
    documento: empresa?.documento || undefined,
    email: empresa?.email || undefined,
    telefone: empresa?.telefone || empresa?.celular || undefined,
    endereco: montarEnderecoCompleto(empresa) || undefined,
});

export const clienteDoLocatario = (locatario: any): ParteFatura | null => {
    if (!locatario?.nome) return null;
    return {
        nome: locatario.nome,
        documento: locatario.documento || undefined,
        email: locatario.email || undefined,
        telefone: locatario.telefone || locatario.celular || undefined,
        endereco: montarEnderecoCompleto(locatario) || undefined,
    };
};

export const descricaoDoVeiculo = (veiculo: any): string | undefined => {
    if (!veiculo) return undefined;
    const nome = [veiculo.marca, veiculo.modelo].filter(Boolean).join(" ");
    const placa = veiculo.placa ? `Placa ${veiculo.placa}` : "";
    const texto = [nome, placa].filter(Boolean).join(" - ");
    return texto || undefined;
};

const dataFormatada = (valor?: string | Date | null): string => {
    if (!valor) return "";
    const data = valor instanceof Date ? valor : new Date(valor);
    return isNaN(data.getTime()) ? "" : data.toLocaleDateString("pt-BR");
};

export const pagamentoEmAberto = (pagamento: Pagamento): boolean => pagamento.status !== StatusPagamento.Pago;

export const lancamentoEmAberto = (lancamento: Lancamento): boolean => lancamento.status !== TipoStatusLancamento.Resolvido;

export const faturaDePagamento = (
    pagamento: Pagamento,
    locacao: any | null,
    empresa: any,
    nomeEmpresaFallback: string,
    valorCobranca?: number,
    emissao: Date = new Date(),
    pix: PixFatura | null = null
): DadosFatura => {
    const valor = valorCobranca ?? pagamento.valor;
    const pagoEm = pagamento.dataPagamentoRealizado ? dataFormatada(pagamento.dataPagamentoRealizado) : "";
    return {
        id: pagamento.id ?? "",
        numero: numeroDaFatura(pagamento.id),
        situacao: situacaoDoPagamento(pagamento.status),
        emissao,
        emissor: emissorDaEmpresa(empresa ?? locacao?.empresa, nomeEmpresaFallback),
        cliente: clienteDoLocatario(locacao?.locatario),
        itens: [{
            descricao: pagamento.descricao || "Pagamento de aluguel",
            detalhe: descricaoDoVeiculo(locacao?.veiculo),
            unidade: "un",
            quantidade: 1,
            valorUnitario: valor,
        }],
        parcelas: [{
            parcela: "1/1",
            forma: formaDePagamento(pagamento.asaasPaymentId),
            vencimento: pagamento.dataPrevista,
            observacoes: pagoEm ? `Pago em ${pagoEm}` : "",
            valor,
            emAberto: pagamentoEmAberto(pagamento),
            pix: pagamentoEmAberto(pagamento) ? pix : null,
        }],
        formaPadrao: formaDePagamento(pagamento.asaasPaymentId),
        condicao: "À vista",
        idFilial: pagamento.idFilial ?? null,
    };
};

export const faturaDeLancamento = (
    lancamento: Lancamento,
    locacao: any | null,
    empresa: any,
    nomeEmpresaFallback: string,
    emissao: Date = new Date(),
    pix: PixFatura | null = null
): DadosFatura => {
    const categorias = (lancamento.lancamentoCategorias ?? [])
        .map((lc: any) => lc?.categoria?.nome ?? lc?.nome)
        .filter(Boolean)
        .join(", ");
    const pagoEm = lancamento.DataPagamentoFoiFeito ? dataFormatada(lancamento.DataPagamentoFoiFeito) : "";
    return {
        id: lancamento.id ?? "",
        numero: numeroDaFatura(lancamento.id),
        situacao: situacaoDoLancamento(lancamento, emissao),
        emissao,
        emissor: emissorDaEmpresa(empresa ?? locacao?.empresa, nomeEmpresaFallback),
        cliente: clienteDoLocatario(locacao?.locatario),
        itens: [{
            descricao: lancamento.descricao || "Lançamento",
            detalhe: [descricaoDoVeiculo(locacao?.veiculo), categorias].filter(Boolean).join(" - ") || undefined,
            unidade: "un",
            quantidade: 1,
            valorUnitario: lancamento.valor,
        }],
        parcelas: [{
            parcela: "1/1",
            forma: formaDePagamento(lancamento.asaasPaymentId),
            vencimento: lancamento.dataRealizado,
            observacoes: pagoEm ? `Pago em ${pagoEm}` : "",
            valor: lancamento.valor,
            emAberto: lancamentoEmAberto(lancamento),
            pix: lancamentoEmAberto(lancamento) ? pix : null,
        }],
        formaPadrao: formaDePagamento(lancamento.asaasPaymentId),
        condicao: "À vista",
        idFilial: lancamento.idFilial ?? null,
    };
};

const chaveDoCliente = (cliente: ParteFatura | null): string =>
    cliente ? (cliente.documento || cliente.nome).trim().toLowerCase() : "";

const chaveDoItem = (item: ItemFatura): string =>
    `${item.descricao}|${item.detalhe ?? ""}|${item.unidade}|${item.valorUnitario}`;

const situacaoUnificada = (situacoes: string[]): string => {
    if (situacoes.length > 0 && situacoes.every(s => s === SITUACAO_PAGA)) return SITUACAO_PAGA;
    if (situacoes.some(s => s === SITUACAO_EM_ATRASO)) return SITUACAO_EM_ATRASO;
    return SITUACAO_EM_ABERTO;
};

export const unificarFaturas = (faturas: DadosFatura[]): DadosFatura => {
    const primeira = faturas[0];
    const itens: ItemFatura[] = [];
    const indicePorChave = new Map<string, number>();
    faturas.forEach(f => f.itens.forEach(item => {
        const chave = chaveDoItem(item);
        const indice = indicePorChave.get(chave);
        if (indice === undefined) {
            indicePorChave.set(chave, itens.length);
            itens.push({ ...item });
        } else {
            itens[indice].quantidade += item.quantidade;
        }
    }));

    const parcelasOrdenadas = faturas
        .reduce<ParcelaFatura[]>((todas, f) => todas.concat(f.parcelas), [])
        .sort((a, b) => dataEmMs(a.vencimento) - dataEmMs(b.vencimento));
    const parcelas = parcelasOrdenadas.map((p, i) => ({ ...p, parcela: `${i + 1}/${parcelasOrdenadas.length}` }));
    const formas = Array.from(new Set(parcelas.map(p => p.forma)));

    return {
        id: primeira.id,
        numero: primeira.numero,
        situacao: situacaoUnificada(faturas.map(f => f.situacao)),
        emissao: primeira.emissao,
        emissor: primeira.emissor,
        cliente: primeira.cliente,
        itens,
        parcelas,
        formaPadrao: formas.length === 1 ? formas[0] : "Conforme parcela",
        condicao: parcelas.length > 1 ? `${parcelas.length} parcelas` : "À vista",
        idFilial: faturas.map(f => f.idFilial).find(Boolean) ?? null,
    };
};

export const comPixUnico = (dados: DadosFatura, pix: PixFatura | null): DadosFatura => ({
    ...dados,
    pixUnico: pix,
    parcelas: dados.parcelas.map(p => ({ ...p, pix: null })),
});

export const unificarPorCliente = (faturas: DadosFatura[]): DadosFatura[] => {
    const grupos = new Map<string, DadosFatura[]>();
    faturas.forEach(f => {
        const chave = chaveDoCliente(f.cliente);
        const grupo = grupos.get(chave);
        if (grupo) grupo.push(f);
        else grupos.set(chave, [f]);
    });
    return Array.from(grupos.values()).map(unificarFaturas);
};

const dataEmMs = (valor?: string | Date | null): number => {
    if (!valor) return Number.MAX_SAFE_INTEGER;
    const data = valor instanceof Date ? valor : new Date(valor);
    return isNaN(data.getTime()) ? Number.MAX_SAFE_INTEGER : data.getTime();
};

export const totalDaFatura = (dados: DadosFatura): number =>
    dados.itens.reduce((soma, item) => soma + item.quantidade * item.valorUnitario, 0);

export const pagamentosComRecibo = (pagamentos: Pagamento[]): { pagos: Pagamento[]; naoPagos: number } => {
    const pagos = pagamentos.filter(p => p.status === StatusPagamento.Pago);
    return { pagos, naoPagos: pagamentos.length - pagos.length };
};

export const lancamentosFaturaveis = (lancamentos: Lancamento[], ids: string[]): { faturaveis: Lancamento[]; saidas: number } => {
    const conjunto = new Set(ids.map(String));
    const exibidos = lancamentos.filter(l => l.id && conjunto.has(String(l.id)));
    const faturaveis = exibidos.filter(l => l.tipo === TipoLancamento.Entrada);
    return { faturaveis, saidas: exibidos.length - faturaveis.length };
};

export const idsDistintos = (valores: (string | null | undefined)[]): string[] =>
    Array.from(new Set(valores.filter((v): v is string => !!v)));
